import { Dexie } from "dexie";
import PopupManager from "../../Common/Popups/PopupManager";
import { setTempOption } from "../../Utilities/Platform/Options/Options";
import { isInUploadWorkerContext } from "../../Utilities/Platform/WorkerHelpers";
import BaseDataSource from "../_Bases/BaseDataSource";
import { type DataSourceTables } from "../_Bases/DataSourceTables";
import type BaseIndexedDBRepository from "../Implementations/BaseIndexedDBRepository";
import IndexedDBApplicationRepository from "../Repositories/Applications/IndexedDBApplicationRepository";
import IndexedDBBarcodeTemplateRepository from "../Repositories/BarcodeTemplates/IndexedDBBarcodeTemplateRepository";
import IndexedDBBrandRepository from "../Repositories/Brands/IndexedDBBrandRepository";
import IndexedDBCategoryRepository from "../Repositories/Categories/IndexedDBCategoryRepository";
import IndexedDBCustomerDisplays from "../Repositories/CustomerDisplays/IndexedDBCustomerDisplayRepository";
import IndexedDBCustomerGroupRepository from "../Repositories/CustomerGroups/IndexedDBCustomerGroupRepository";
import IndexedDBCustomerRepository from "../Repositories/Customers/IndexedDBCustomerRepository";
import IndexedDBEnterpriseRepository from "../Repositories/Enterprises/IndexedDBEnterpriseRepository";
import IndexedDBFamilyRepository from "../Repositories/Families/IndexedDBFamilyRepository";
import IndexedDBGiftCardRepository from "../Repositories/GiftCards/IndexedDBGiftCardRepository";
import IndexedDBKitchenOrderRepository from "../Repositories/KitchenOrders/IndexedDBKitchenOrderRepository";
import IndexedDBKitchenScreenRepository from "../Repositories/KitchenScreens/IndexedDBKitchenScreenRepository";
import IndexedDBLockedResourceRepository from "../Repositories/LockedResources/IndexedDBLockedResourceRepository";
import IndexedDBLoyaltyRepository from "../Repositories/Loyalty/IndexedDBLoyaltyRepository";
import IndexedDBMovementRepository from "../Repositories/Movements/IndexedDBMovementRepository";
import IndexedDBOutletRepository from "../Repositories/Outlets/IndexedDBOutletRepository";
import IndexedDBPageRuleRepository from "../Repositories/PageRules/IndexedDBPageRuleRepository";
import IndexedDBPaymentMethodRepository from "../Repositories/PaymentMethods/IndexedDBPaymentMethodRepository";
import IndexedDBPriceListRepository from "../Repositories/PriceLists/IndexedDBPriceListRepository";
import IndexedDBPriceSetsRepository from "../Repositories/PriceSets/IndexedDBPriceSetsRepository";
import IndexedDBProductRepository from "../Repositories/Products/IndexedDBProductRepository";
import IndexedDBPromotionCategoryRepository
    from "../Repositories/PromotionCategories/IndexedDBPromotionCategoryRepository";
import IndexedDBPromotionRepository from "../Repositories/Promotions/IndexedDBPromotionRepository";
import IndexedDBReceiptRepository from "../Repositories/Receipts/IndexedDBReceiptRepository";
import IndexedDBRegisterRepository from "../Repositories/Registers/IndexedDBRegisterRepository";
import IndexedDBSalesRepository from "../Repositories/Sales/IndexedDBSalesRepository";
import IndexedDBSalesKeyRepository from "../Repositories/SalesKeys/IndexedDBSalesKeyRepository";
import IndexedDBStocktakeAccumulatedRepository
    from "../Repositories/StocktakeAccumulated/IndexedDBStocktakeAccumulatedRepository";
import IndexedDBStocktakesRepository from "../Repositories/Stocktakes/IndexedDBStocktakesRepository";
import IndexedDBStocktakeScannedRepository from "../Repositories/StocktakeScanned/IndexedDBStocktakeScannedRepository";
import IndexedDBSupplierRepository from "../Repositories/Suppliers/IndexedDBSupplierRepository";
import IndexedDBSystemRepository from "../Repositories/System/IndexedDBSystemRepository";
import IndexedDBTagRepository from "../Repositories/Tags/IndexedDBTagRepository";
import IndexedDBTakingRepository from "../Repositories/Takings/IndexedDBTakingRepository";
import IndexedDBTaxRateRepository from "../Repositories/TaxRates/IndexedDBTaxRateRepository";
import IndexedDBTransfereeRepository from "../Repositories/Transferees/IndexedDBTransfereeRepository";
import IndexedDBUploadRepository from "../Repositories/Uploads/IndexedDBUploadRepository";
import IndexedDBUserRepository from "../Repositories/Users/IndexedDBUserRepository";
import IndexedDBVendorConnectionRepository from "../Repositories/VendorConnections/IndexedDBVendorConnectionRepository";

export class InternalIndexedDBDatabase extends Dexie {
    public products: Dexie.Table<any, string>;
    public users: Dexie.Table<any, string>;
    public promotions: Dexie.Table<any, string>;
    public promotionCategories: Dexie.Table<any, string>;
    public customers: Dexie.Table<any, string>;
    public customerGroups: Dexie.Table<any, string>;
    public priceLists: Dexie.Table<any, string>;
    public paymentMethods: Dexie.Table<any, string>;
    public sales: Dexie.Table<any, string>;
    public salesKeys: Dexie.Table<any, string>;
    public receipts: Dexie.Table<any, string>;
    public giftCards: Dexie.Table<any, string>;
    public loyalty: Dexie.Table<any, number>;
    public customerDisplays: Dexie.Table<any, string>;
    public brands: Dexie.Table<any, string>;
    public categories: Dexie.Table<any, string>;
    public families: Dexie.Table<any, string>;
    public tags: Dexie.Table<any, string>;
    public outlets: Dexie.Table<any, string>;
    public registers: Dexie.Table<any, string>;
    public taxRates: Dexie.Table<any, string>;
    public uploads: Dexie.Table<any, number>;
    public takings: Dexie.Table<any, string>;
    public pageRules: Dexie.Table<any, string>;
    public suppliers: Dexie.Table<any, string>;
    public priceSets: Dexie.Table<any, string>;
    public transferees: Dexie.Table<any, string>;
    public movements: Dexie.Table<any, string>;
    public stocktakes: Dexie.Table<any, string>;
    public stocktakeScanned: Dexie.Table<any, number>;
    public stocktakeAccumulated: Dexie.Table<any, string>;
    public applications: Dexie.Table<any, string>;
    public barcodeTemplates: Dexie.Table<any, number>;
    public vendorConnections: Dexie.Table<any, string>;
    public lockedResources: Dexie.Table<any, string>;
    public system: Dexie.Table<any, string>;
    public enterprises: Dexie.Table<any, string>;
    public kitchenOrders: Dexie.Table<any, string>;
    public kitchenScreens: Dexie.Table<any, string>;

    constructor() {
        super("shopfront-pos");

        this.version(1).stores({
            products:
                "uuid, status, name, *name_index, type, category_id, brand_id, family_id, *tags, *code_index," +
                " *supplier_ids, *supplier_codes",
            users              : "uuid, username, name",
            promotions         : "uuid, name, start, end, available_to",
            promotionCategories: "uuid, name",
            customers          : "uuid, *name_index, email, code, *phones, customer_group_id, company, full_name," +
                " pricelist_id",
            customerGroups      : "uuid, name",
            priceLists          : "uuid, name",
            paymentMethods      : "uuid, name, type",
            sales               : "id, status, invoiceId, createdAt",
            salesKeys           : "uuid, keys",
            receipts            : "uuid, type, account_receipt",
            giftCards           : "uuid, code",
            loyalty             : "++id, type, uuid, [type+uuid]",
            promotionCache      : "id, version, promotion, *products",
            customerDisplays    : "uuid",
            brands              : "uuid, name",
            categories          : "uuid, name",
            families            : "uuid, name",
            tags                : "uuid, name",
            outlets             : "uuid",
            registers           : "uuid",
            taxRates            : "uuid, name",
            uploads             : "++id, type, attempts",
            takings             : "uuid, type",
            pageRules           : "type, rule",
            suppliers           : "uuid, name",
            priceSets           : "uuid",
            transferees         : "uuid, name",
            movements           : "uuid",
            stocktakes          : "uuid, name, outlet_id",
            stocktakeScanned    : "++id, stocktake_id",
            stocktakeAccumulated: "uuid, stocktake_id",
            applications        : "id, name",
        });

        this.version(2).stores({
            promotions: "uuid, name, start, end, available_to, promotion_category_id",
        });

        this.version(3).stores({
            sales: "id, status, invoiceId, createdAt, clientId",
        });

        this.version(4).stores({
            barcodeTemplates: "template",
        });

        this.version(5).stores({
            vendorConnections: "uuid, name",
        });

        this.version(6).stores({
            lockedResources: "uuid, resource",
        });

        this.version(7).stores({
            system: "key",
        });

        this.version(8).stores({
            products:
                "uuid, status, name, *name_index, type, category_id, brand_id, family_id, *tags, *code_index," +
                " *supplier_ids, *supplier_codes, mdb_id",
        });

        this.version(9).stores({
            enterprises: "id, name",
        });

        this.version(10).stores({
            kitchenOrders : "id, version",
            kitchenScreens: "uuid, name",
        });

        this.products = this.table("products");
        this.users = this.table("users");
        this.promotions = this.table("promotions");
        this.promotionCategories = this.table("promotionCategories");
        this.customers = this.table("customers");
        this.customerGroups = this.table("customerGroups");
        this.priceLists = this.table("priceLists");
        this.paymentMethods = this.table("paymentMethods");
        this.sales = this.table("sales");
        this.salesKeys = this.table("salesKeys");
        this.receipts = this.table("receipts");
        this.giftCards = this.table("giftCards");
        this.loyalty = this.table("loyalty");
        this.customerDisplays = this.table("customerDisplays");
        this.brands = this.table("brands");
        this.categories = this.table("categories");
        this.families = this.table("families");
        this.tags = this.table("tags");
        this.outlets = this.table("outlets");
        this.registers = this.table("registers");
        this.taxRates = this.table("taxRates");
        this.uploads = this.table("uploads");
        this.takings = this.table("takings");
        this.pageRules = this.table("pageRules");
        this.suppliers = this.table("suppliers");
        this.priceSets = this.table("priceSets");
        this.transferees = this.table("transferees");
        this.movements = this.table("movements");
        this.stocktakes = this.table("stocktakes");
        this.stocktakeScanned = this.table("stocktakeScanned");
        this.stocktakeAccumulated = this.table("stocktakeAccumulated");
        this.applications = this.table("applications");
        this.barcodeTemplates = this.table("barcodeTemplates");
        this.vendorConnections = this.table("vendorConnections");
        this.lockedResources = this.table("lockedResources");
        this.system = this.table("system");
        this.enterprises = this.table("enterprises");
        this.kitchenOrders = this.table("kitchenOrders");
        this.kitchenScreens = this.table("kitchenScreens");
    }
}

const db = new InternalIndexedDBDatabase();

// Open the database
let popupDatabaseWarning = false;
let globalOnVersionUpgrade: undefined | ((forceAllRefresh: boolean) => void);
let globalOnBlocked: undefined | ((certainItIsBlocked: boolean) => void);
let hasOpenedAtSomePoint = false;
let openAttempts = 0;
let stopAttemptsToOpen = false;
const openDatabase = (): void => {
    if(!hasOpenedAtSomePoint) {
        if(openAttempts === 3) {
            // Looks like an issue opening the database, going to guess it's blocked but hasn't thrown
            // an error
            if(typeof globalOnBlocked === "function") {
                stopAttemptsToOpen = true;
                globalOnBlocked(false);
            } else {
                PopupManager.error(
                    "We were unable to open the local database, try refreshing the page. If the issue continues " +
                    "please reach out to Shopfront"
                );
            }
        }

        openAttempts++;
    }

    db.open()
        .then(async () => {
            if(!hasOpenedAtSomePoint) {
                setTempOption("feature:database-blocked-refresh", true);
            }

            hasOpenedAtSomePoint = true;
            openAttempts = 0;

            if(!isInUploadWorkerContext()) {
                return;
            }

            try {
                // Ensure all outstanding sales are waiting to upload
                const uploads = await db.uploads.where("type").equals("sale").toArray();
                const sales = (await db.sales.toArray()).filter(sale => sale.id.includes("-"));
                const updates = [];

                for(let i = 0, l = sales.length; i < l; i++) {
                    let found = false;

                    for(let j = 0, n = uploads.length; j < n; j++) {
                        if(uploads[j].data === sales[i].id) {
                            found = true;
                            break;
                        }
                    }

                    if(found) {
                        continue;
                    }

                    updates.push({
                        data     : sales[i].id,
                        requestId: `${i}-${Date.now()}`,
                        attempts : 0,
                        type     : "sale",
                    });
                }

                if(updates.length) {
                    console.log(`Adding ${updates.length} missing sales to upload queue`);
                    await db.uploads.bulkPut(updates);
                }
            } catch {
                // Do nothing
            }
        })
        .catch((err) => {
            console.error("Could not open local database.", err);

            if(
                err instanceof Dexie.VersionError &&
                !popupDatabaseWarning &&
                typeof globalOnVersionUpgrade !== "undefined"
            ) {
                popupDatabaseWarning = true;
                globalOnVersionUpgrade(false);

                return;
            }

            PopupManager.error(
                "The local database received an error while opening, please contact Shopfront if this issue persists"
            );
        });
};

openDatabase();

// Catch closed database errors
globalThis.addEventListener("unhandledrejection", event => {
    if(event.reason && event.reason.name === Dexie.errnames.DatabaseClosed) {
        console.debug("Received a database closed error, attempting to reopen now");
        openDatabase();
    }
});

// Check every five seconds to ensure the database is open
const openInterval = setInterval(() => {
    if(stopAttemptsToOpen) {
        clearInterval(openInterval);

        return;
    }

    if(!db.isOpen()) {
        console.debug("Database is closed, opening now (found on interval)");
        openDatabase();
    }
}, 5000);

// Check if we're upgrading the version
db.on("versionchange", (e) => {
    if(isInUploadWorkerContext()) {
        return;
    }

    if(!e.newVersion) {
        PopupManager.error("The local database has been deleted, please contact Shopfront if this was unexpected");

        return;
    }

    if(
        !popupDatabaseWarning &&
        typeof globalOnVersionUpgrade !== "undefined"
    ) {
        globalOnVersionUpgrade(true);
    }

    return false;
});

// Check if we've been blocked from opening the database
db.on("blocked", e => {
    stopAttemptsToOpen = true;

    if(isInUploadWorkerContext()) {
        return;
    }

    if(!e.newVersion || e.newVersion < e.oldVersion) {
        PopupManager.info("Deletion of the database was blocked");

        return;
    }

    if(typeof globalOnBlocked === "undefined") {
        if(typeof globalOnVersionUpgrade === "undefined") {
            throw new Error("Database blocked from upgrading, but no handler was present to resolve the issue");
        }

        globalOnVersionUpgrade(false);

        return;
    }

    openAttempts = 4; // To not override the open attempts
    globalOnBlocked(true);
});

// Add Dexie to the global object (for support)
(globalThis as any)._$database = db;

// Create the IndexedDB Data Source
export class IndexedDBDataSource extends BaseDataSource {
    protected source: InternalIndexedDBDatabase;

    constructor(
        source: InternalIndexedDBDatabase,
        onVersionUpgrade?: (forceAllRefresh: boolean) => void,
        onUpgradeBlocked?: (certainItIsBlocked: boolean) => void
    ) {
        super();

        this.source = source;
        globalOnVersionUpgrade = onVersionUpgrade;
        globalOnBlocked = onUpgradeBlocked;

        this.products = new IndexedDBProductRepository(this.source);
        this.customers = new IndexedDBCustomerRepository(this.source);
        this.customerGroups = new IndexedDBCustomerGroupRepository(this.source);
        this.promotions = new IndexedDBPromotionRepository(this.source);
        this.promotionCategories = new IndexedDBPromotionCategoryRepository(this.source);
        this.users = new IndexedDBUserRepository(this.source);
        this.priceLists = new IndexedDBPriceListRepository(this.source);
        this.paymentMethods = new IndexedDBPaymentMethodRepository(this.source);
        this.salesKeys = new IndexedDBSalesKeyRepository(this.source);
        this.receipts = new IndexedDBReceiptRepository(this.source);
        this.giftCards = new IndexedDBGiftCardRepository(this.source);
        this.loyalty = new IndexedDBLoyaltyRepository(this.source);
        this.customerDisplays = new IndexedDBCustomerDisplays(this.source);
        this.sales = new IndexedDBSalesRepository(this.source);
        this.brands = new IndexedDBBrandRepository(this.source);
        this.categories = new IndexedDBCategoryRepository(this.source);
        this.families = new IndexedDBFamilyRepository(this.source);
        this.tags = new IndexedDBTagRepository(this.source);
        this.outlets = new IndexedDBOutletRepository(this.source);
        this.registers = new IndexedDBRegisterRepository(this.source);
        this.uploads = new IndexedDBUploadRepository(this.source);
        this.taxRates = new IndexedDBTaxRateRepository(this.source);
        this.takings = new IndexedDBTakingRepository(this.source);
        this.pageRules = new IndexedDBPageRuleRepository(this.source);
        this.suppliers = new IndexedDBSupplierRepository(this.source);
        this.priceSets = new IndexedDBPriceSetsRepository(this.source);
        this.transferees = new IndexedDBTransfereeRepository(this.source);
        this.movements = new IndexedDBMovementRepository(this.source);
        this.stocktakes = new IndexedDBStocktakesRepository(this.source);
        this.stocktakeScanned = new IndexedDBStocktakeScannedRepository(this.source);
        this.stocktakeAccumulated = new IndexedDBStocktakeAccumulatedRepository(this.source);
        this.applications = new IndexedDBApplicationRepository(this.source);
        this.barcodeTemplates = new IndexedDBBarcodeTemplateRepository(this.source);
        this.vendorConnections = new IndexedDBVendorConnectionRepository(this.source);
        this.lockedResources = new IndexedDBLockedResourceRepository(this.source);
        this.system = new IndexedDBSystemRepository(this.source);
        this.enterprises = new IndexedDBEnterpriseRepository(this.source);
        this.kitchenOrders = new IndexedDBKitchenOrderRepository(this.source);
        this.kitchenScreens = new IndexedDBKitchenScreenRepository(this.source);
    }

    public clearDatabase(): Promise<void> {
        return this.source.delete()
            .catch(err => {
                console.error("Could not clear local database.", err);
            });
    }

    public transaction(tables: Array<keyof DataSourceTables>, callback: () => Promise<void>): Promise<void> {
        return this.source.transaction(
            "rw",
            tables.map(table => (this[table] as BaseIndexedDBRepository<any>).source),
            callback
        );
    }

    private static getLimitedCollection<T>(
        table: Dexie.Table<T, string>,
        limit: number,
        offset = 0,
        sortIndex?: keyof T & string
    ): Dexie.Collection<T> {
        let collection: Dexie.Table | Dexie.Collection = table;

        if(typeof sortIndex === "string") {
            collection = collection.orderBy(sortIndex);
        }

        return collection.offset(offset)
            .limit(limit);
    }

    public static async each<T>(
        table: Dexie.Table<T, string>,
        callback: (item: T) => void,
        sortIndex?: keyof T & string,
        pageSize = 500
    ): Promise<void> {
        let page = await IndexedDBDataSource.getLimitedCollection(
            table,
            pageSize,
            0,
            sortIndex
        ).toArray();

        page.forEach(callback);

        let offset = page.length;

        while(page.length === pageSize) {
            page = await IndexedDBDataSource.getLimitedCollection(
                table,
                pageSize,
                offset,
                sortIndex
            ).toArray();

            page.forEach(callback);

            offset += page.length;
        }
    }

    public async getVersion(): Promise<number | undefined> {
        return this.source.verno;
    }
}

export class IndexedDatabase {
    public initialised: IndexedDBDataSource;

    constructor(
        onVersionUpgrade?: (forceAllRefresh: boolean) => void,
        onUpgradeBlocked?: (certainItIsBlocked: boolean) => void
    ) {
        this.initialised = new IndexedDBDataSource(db, onVersionUpgrade, onUpgradeBlocked);
    }
}
